import React, { useRef, useState, useEffect } from "react";
import { Box, Typography, IconButton, alpha, Chip } from "@mui/material";
import { ChevronLeft, ChevronRight, LocalOffer } from "@mui/icons-material";
import FileServices from "../../../services/FileServices";
import OfferItem from "./OfferItem";
import OfferDetailsDialog from "./OfferDetailsDialog";

const OfferCarousel = ({ offers, onAddToCart, hideHeader = false }) => {
  const scrollContainerRef = useRef(null);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [imageUrls, setImageUrls] = useState({});
  const [selectedType, setSelectedType] = useState("all");

  // Filter offers based on date range
  const isOfferValid = (offer) => {
    // If no date range specified, show always
    if (!offer.startDate && !offer.endDate) {
      return true;
    }

    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0); // Reset time to start of day for comparison

    // Check if current date is within the range
    if (offer.startDate && offer.endDate) {
      const startDate = new Date(offer.startDate);
      startDate.setHours(0, 0, 0, 0);
      const endDate = new Date(offer.endDate);
      endDate.setHours(23, 59, 59, 999); // End of day

      return currentDate >= startDate && currentDate <= endDate;
    }

    // If only startDate is specified
    if (offer.startDate) {
      const startDate = new Date(offer.startDate);
      startDate.setHours(0, 0, 0, 0);
      return currentDate >= startDate;
    }

    // If only endDate is specified
    if (offer.endDate) {
      const endDate = new Date(offer.endDate);
      endDate.setHours(23, 59, 59, 999);
      return currentDate <= endDate;
    }

    return true;
  };

  const validOffers = offers.filter(isOfferValid).filter((offer) => {
    if (selectedType === "all") return true;
    return offer.type?.toLowerCase() === selectedType;
  });

  useEffect(() => {
    const loadImages = async () => {
      const urls = {};

      for (const offer of validOffers) {
        if (offer.image) {
          try {
            const blob = await FileServices.getImageBlob(offer.image);
            urls[offer.id] = URL.createObjectURL(blob);
          } catch (error) {
            console.error(`Error loading offer image for ${offer.id}:`, error);
          }
        }

        // Load menu item images if available
        if (offer.OfferItems) {
          for (const offerItem of offer.OfferItems) {
            if (offerItem.MenuItem?.image) {
              try {
                const blob = await FileServices.getImageBlob(
                  offerItem.MenuItem.image,
                );
                urls[offerItem.MenuItem.id] = URL.createObjectURL(blob);
              } catch (error) {
                console.error(
                  `Error loading menu item image for ${offerItem.MenuItem.id}:`,
                  error,
                );
              }
            }
          }
        }
      }

      setImageUrls(urls);
    };

    loadImages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offers]);

  const handleOpenDetails = (offer) => {
    setSelectedOffer(offer);
    setDetailsDialogOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsDialogOpen(false);
    setSelectedOffer(null);
  };

  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = 300;
      const newScrollLeft =
        scrollContainerRef.current.scrollLeft +
        (direction === "left" ? -scrollAmount : scrollAmount);
      scrollContainerRef.current.scrollTo({
        left: newScrollLeft,
        behavior: "smooth",
      });
    }
  };

  // Don't render anything if no offers at all (not even date-invalid ones)
  if (offers.length === 0) {
    return null;
  }

  return (
    <Box sx={{ position: "relative", mb: 4 }}>
      {!hideHeader && (
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "flex-start", md: "center" },
            justifyContent: "space-between",
            mb: 3,
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                width: 50,
                height: 50,
                borderRadius: "14px",
                background: `linear-gradient(135deg, #f59e0b 0%, #d97706 100%)`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: `0 4px 12px ${alpha("#f59e0b", 0.3)}`,
              }}
            >
              <LocalOffer sx={{ fontSize: 26, color: "white" }} />
            </Box>
            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: "#374151",
                  lineHeight: 1.2,
                }}
              >
                Special Offers
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "#9ca3af", fontSize: "0.85rem" }}
              >
                {validOffers.length}{" "}
                {validOffers.length === 1 ? "offer" : "offers"} available •
                Swipe to explore
              </Typography>
            </Box>
          </Box>

          {/* Navigation Buttons */}
          <Box sx={{ display: { xs: "none", md: "flex" }, gap: 1 }}>
            <IconButton
              onClick={() => scroll("left")}
              sx={{
                width: 40,
                height: 40,
                bgcolor: "#ffffff",
                border: "none",
                boxShadow: `0 2px 8px ${alpha("#000000", 0.08)}`,
                "&:hover": {
                  bgcolor: "#f9fafb",
                  transform: "translateY(-2px)",
                  boxShadow: `0 4px 12px ${alpha("#000000", 0.12)}`,
                },
                transition: "all 0.2s ease",
              }}
            >
              <ChevronLeft />
            </IconButton>
            <IconButton
              onClick={() => scroll("right")}
              sx={{
                width: 40,
                height: 40,
                bgcolor: "#ffffff",
                border: "none",
                boxShadow: `0 2px 8px ${alpha("#000000", 0.08)}`,
                "&:hover": {
                  bgcolor: "#f9fafb",
                  transform: "translateY(-2px)",
                  boxShadow: `0 4px 12px ${alpha("#000000", 0.12)}`,
                },
                transition: "all 0.2s ease",
              }}
            >
              <ChevronRight />
            </IconButton>
          </Box>
        </Box>
      )}

      {/* Type Filter Chips */}
      <Box
        sx={{
          display: "flex",
          gap: 1.5,
          mb: 3,
          flexWrap: "wrap",
        }}
      >
        {["all", "combo", "bundle", "promotion"].map((type) => {
          const typeLabels = {
            all: "All Offers",
            combo: "Combo Deals",
            bundle: "Bundle Offers",
            promotion: "Promotions",
          };

          const offerCount =
            type === "all"
              ? offers.filter(isOfferValid).length
              : offers
                  .filter(isOfferValid)
                  .filter((o) => o.type?.toLowerCase() === type).length;

          return (
            <Chip
              key={type}
              label={`${typeLabels[type]} (${offerCount})`}
              onClick={() => setSelectedType(type)}
              sx={{
                height: 36,
                fontSize: "0.85rem",
                fontWeight: 600,
                borderRadius: "12px",
                px: 1.5,
                bgcolor: selectedType === type ? "#f59e0b" : "#ffffff",
                color: selectedType === type ? "#ffffff" : "#6b7280",
                border: "none",
                boxShadow:
                  selectedType === type
                    ? `0 4px 12px ${alpha("#f59e0b", 0.3)}`
                    : `0 2px 6px ${alpha("#000000", 0.06)}`,
                "&:hover": {
                  bgcolor: selectedType === type ? "#d97706" : "#f9fafb",
                  transform: "translateY(-2px)",
                  boxShadow:
                    selectedType === type
                      ? `0 6px 16px ${alpha("#f59e0b", 0.4)}`
                      : `0 3px 8px ${alpha("#000000", 0.08)}`,
                },
                transition: "all 0.2s ease",
                cursor: "pointer",
              }}
            />
          );
        })}
      </Box>

      {/* Carousel Container or Empty State */}
      {validOffers.length > 0 ? (
        <Box
          ref={scrollContainerRef}
          sx={{
            display: "flex",
            gap: 3,
            overflowX: "auto",
            overflowY: "visible",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": {
              display: "none",
            },
            pb: 2,
            pt: 1,
            px: 0.5,
          }}
        >
          {validOffers.map((item) => (
            <OfferItem
              key={item.id}
              item={item}
              imageUrl={imageUrls[item.id]}
              onAddToCart={onAddToCart}
              onViewDetails={handleOpenDetails}
            />
          ))}
        </Box>
      ) : (
        <Box
          sx={{
            textAlign: "center",
            py: 6,
            px: 3,
            bgcolor: alpha("#f59e0b", 0.05),
            borderRadius: 3,
            border: `2px dashed ${alpha("#f59e0b", 0.2)}`,
          }}
        >
          <LocalOffer
            sx={{
              fontSize: 48,
              color: alpha("#f59e0b", 0.4),
              mb: 2,
            }}
          />
          <Typography
            variant="h6"
            sx={{
              color: "#6b7280",
              fontWeight: 600,
              mb: 1,
            }}
          >
            No offers available
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "#9ca3af",
            }}
          >
            Try selecting a different category or check back later for new deals
          </Typography>
        </Box>
      )}

      {/* Offer Details Dialog */}
      <OfferDetailsDialog
        open={detailsDialogOpen}
        onClose={handleCloseDetails}
        offer={selectedOffer}
        imageUrls={imageUrls}
        onAddToCart={onAddToCart}
      />
    </Box>
  );
};

export default OfferCarousel;
